import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import { DateTimeFormats } from 'brock-date-time';

export const predicateValues = {
    isNot: 1,
    is: 2,
    contains: 3,
    beginsWith: 4,
    endsWith: 5,
    notIn: 6,
    isIn: 7,
    greaterThan: 8,
    greaterThanOrEqualTo: 9,
    lessThan: 10,
    lessThanOrEqualTo: 11,
};

function getTimeAndPrecisionLinkProperties(timeAndPrecision) {
    const {
        timeAndPrecisionType,
        startDateTime,
        endDateTime,
        rangeType,
        rangeValue,
        predefinedDateRange,
        selectedDateRangeOption,
    } = timeAndPrecision;

    return {
        timeAndPrecisionType,
        startDateTime,
        endDateTime,
        rangeType,
        rangeValue,
        predefinedDateRange,
        selectedDateRangeOption,
    };
}

function buildTimeAndPrecisionArgument(
    timeAndPrecision = {},
    argumentConfig = {}
) {
    return {
        argument: getTimeAndPrecisionLinkProperties(timeAndPrecision),
        fieldName: argumentConfig.fieldName,
        predicate: '',
    };
}

function buildDataRowArgument(
    dataRow = {},
    timeAndPrecision = {},
    argumentConfig = {},
    predicateLabels = {}
) {
    if (!dataRow[argumentConfig.dashboardKey]) {
        // eslint-disable-next-line no-console
        console.log(
            `DataRow did not include field: ${argumentConfig.dashboardKey}`
        );
        return null;
    }

    if (argumentConfig.useAsTimeAndPrecisionStartDateTime) {
        const updatedTimeAndPrecision = {
            ...timeAndPrecision,
            timeAndPrecisionType: 'fixedDay',
            startDateTime: dataRow[argumentConfig.dashboardKey],
            endDateTime: moment(dataRow[argumentConfig.dashboardKey])
                .startOf('day')
                .add(1, 'day')
                .format(DateTimeFormats.DATETIME),
        };

        return {
            argument: getTimeAndPrecisionLinkProperties(
                updatedTimeAndPrecision
            ),
            fieldName: argumentConfig.advancedSearchFieldName,
            predicate: '',
        };
    }

    return {
        argument: [
            {
                textField: dataRow[argumentConfig.dashboardKey],
                valueField: dataRow[argumentConfig.dashboardKey],
            },
        ],
        fieldName: argumentConfig.advancedSearchFieldName,
        predicate: {
            textField: predicateLabels.isIn,
            valueField: predicateValues.isIn,
        },
    };
}

function buildFilterValueArgument(
    filter = {},
    argumentConfig = {},
    predicateLabels = {}
) {
    if (isEmpty(filter.values) || !filter.predicate) {
        // eslint-disable-next-line no-console
        console.log(`Filter has no values: ${argumentConfig.dashboardKey}`);
        return null;
    }

    return {
        argument: filter.values,
        fieldName: argumentConfig.advancedSearchFieldName,
        predicate: {
            textField: predicateLabels[filter.predicate.valueField],
            valueField: predicateValues[filter.predicate.valueField],
        },
    };
}

function buildWhereClauseArgument(
    whereClause = {},
    argumentConfig = {},
    predicateLabels = {}
) {
    if (isEmpty(whereClause.values) || !whereClause.predicate) {
        // eslint-disable-next-line no-console
        console.log(
            `WhereClause has no values: ${argumentConfig.dashboardKey}`
        );
        return null;
    }

    return {
        argument: whereClause.values[0],
        fieldName: argumentConfig.advancedSearchFieldName,
        predicate: {
            textField: predicateLabels[whereClause.predicate.valueField],
            valueField: predicateValues[whereClause.predicate.valueField],
        },
    };
}

function buildFixedArgument(argumentConfig = {}, predicateLabels = {}) {
    if (isEmpty(argumentConfig.values) || !argumentConfig.predicate) {
        // eslint-disable-next-line no-console
        console.log(
            `Fixed argument has no values: ${argumentConfig.fieldName}`
        );
        return null;
    }

    return {
        argument: argumentConfig.values,
        fieldName: argumentConfig.fieldName,
        predicate: {
            textField: predicateLabels[argumentConfig.predicate.valueField],
            valueField: predicateValues[argumentConfig.predicate.valueField],
        },
    };
}

export function buildQueryConfiguration(
    timeAndPrecision,
    filters,
    whereClauses,
    dataRow,
    argumentConfigs,
    predicateLabels
) {
    const allArguments = [];

    argumentConfigs.forEach((argumentConfig) => {
        switch (argumentConfig.valueOrigin) {
            case 'fixed': {
                allArguments.push(
                    buildFixedArgument(argumentConfig, predicateLabels)
                );
                break;
            }

            case 'timeAndPrecision': {
                allArguments.push(
                    buildTimeAndPrecisionArgument(
                        timeAndPrecision,
                        argumentConfig
                    )
                );
                break;
            }

            case 'filters': {
                const filter = filters[argumentConfig.dashboardKey];
                allArguments.push(
                    buildFilterValueArgument(
                        filter,
                        argumentConfig,
                        predicateLabels
                    )
                );
                break;
            }

            case 'where': {
                const whereClause = whereClauses.find(
                    (w) => w.propertyName === argumentConfig.dashboardKey
                );
                allArguments.push(
                    buildWhereClauseArgument(
                        whereClause,
                        argumentConfig,
                        predicateLabels
                    )
                );
                break;
            }

            case 'dataRow': {
                allArguments.push(
                    buildDataRowArgument(
                        dataRow,
                        timeAndPrecision,
                        argumentConfig,
                        predicateLabels
                    )
                );
                break;
            }

            default: {
                break;
            }
        }
    });

    return {
        queryConfiguration: [
            {
                configuration: allArguments.filter((x) => x),
                isDefault: false,
                missingRequiredFieldNames: [],
            },
        ],
    };
}
